import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { User } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-profile-settings',
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss']
})
export class ProfileSettingsComponent implements OnInit {
  currentUser: User | null = null;
  isLoading = true;
  isSaving = false;
  
  // Form data
  profileForm = {
    username: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadUserProfile();
  }

  private loadUserProfile(): void {
    this.isLoading = true;
    this.userService.getCurrentUserProfile().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.profileForm.username = user.username || '';
        this.profileForm.email = user.email || '';
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading user profile:', error);
        this.toastr.error('Error loading profile data');
        this.isLoading = false;
        
        // Fallback to localStorage if API fails
        try {
          const userData = localStorage.getItem('user-data');
          if (userData) {
            this.currentUser = JSON.parse(userData);
            this.profileForm.username = this.currentUser?.username || '';
            this.profileForm.email = this.currentUser?.email || '';
          }
        } catch (fallbackError) {
          console.error('Fallback to localStorage also failed:', fallbackError);
        }
      }
    });
  }

  onSubmit(): void {
    if (this.validateForm()) {
      this.saveProfile();
    }
  }

  private validateForm(): boolean {
    if (!this.profileForm.username.trim()) {
      this.toastr.error('Username is required');
      return false;
    }

    if (!this.profileForm.email.trim()) {
      this.toastr.error('Email is required');
      return false;
    }

    // Password validation - only if user wants to change password
    if (this.profileForm.newPassword || this.profileForm.confirmPassword) {
      if (!this.profileForm.currentPassword.trim()) {
        this.toastr.error('Current password is required to change password');
        return false;
      }

      if (!this.profileForm.newPassword.trim()) {
        this.toastr.error('New password is required');
        return false;
      }

      if (this.profileForm.newPassword !== this.profileForm.confirmPassword) {
        this.toastr.error('New passwords do not match');
        return false;
      }

      if (this.profileForm.newPassword.length < 6) {
        this.toastr.error('New password must be at least 6 characters long');
        return false;
      }

      if (this.profileForm.currentPassword === this.profileForm.newPassword) {
        this.toastr.error('New password must be different from current password');
        return false;
      }
    }

    return true;
  }

  private saveProfile(): void {
    this.isSaving = true;
    
    const profileData = {
      username: this.profileForm.username,
      email: this.profileForm.email,
      currentPassword: this.profileForm.currentPassword || null,
      newPassword: this.profileForm.newPassword || null
    };
    
    this.userService.updateCurrentUserProfile(profileData).subscribe({
      next: (response) => {
        this.currentUser = response.user;
        
        // Update localStorage with new user data
        localStorage.setItem('user-data', JSON.stringify(response.user));
        
        this.toastr.success(response.message || 'Profile updated successfully');
        
        // Clear password fields
        this.profileForm.currentPassword = '';
        this.profileForm.newPassword = '';
        this.profileForm.confirmPassword = '';
        
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving profile:', error);
        const errorMessage = error.error?.message || 'Error saving profile';
        this.toastr.error(errorMessage);
        this.isSaving = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  getUserRole(): string {
    if (!this.currentUser?.roles || this.currentUser.roles.length === 0) {
      return 'User';
    }
    
    // Handle both formats: 'admin' from API and 'ROLE_ADMIN' from localStorage
    let role = this.currentUser.roles[0];
    if (role.startsWith('ROLE_')) {
      role = role.replace('ROLE_', '').toLowerCase();
    } else {
      role = role.toLowerCase();
    }
    
    if (role === 'admin' || role === 'superadmin') return 'Administrator';
    if (role === 'manager') return 'Manager';
    if (role === 'user' || role === 'employee') return 'User';
    
    return role.charAt(0).toUpperCase() + role.slice(1);
  }
} 